/**
 * Date utility functions for handling UTC timestamps
 * 
 * IMPORTANT: Server timestamps from .NET are returned without the 'Z' suffix,
 * which causes JavaScript to interpret them as local time instead of UTC.
 * These functions ensure proper UTC parsing for consistent time display.
 */

/**
 * Parses a UTC timestamp string and returns a Date object.
 * Handles timestamps with or without the 'Z' suffix.
 * 
 * @param {string|Date} dateString - The UTC timestamp string (e.g., "2024-12-19T11:15:00" or "2024-12-19T11:15:00Z") or Date object
 * @returns {Date|null} - A Date object or null if invalid
 */
export function parseUtcDate(dateString) {
  if (!dateString) return null;
  
  // If already a Date object, return it
  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? null : dateString;
  }
  
  // If not a string, try to convert
  if (typeof dateString !== 'string') {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // If the string doesn't end with 'Z' and doesn't have a timezone offset, append 'Z'
  // This ensures JavaScript interprets the timestamp as UTC, not local time
  let normalizedString = dateString;
  if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
    normalizedString = dateString + 'Z';
  }
  
  const date = new Date(normalizedString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a UTC timestamp string to a localized date/time string.
 * 
 * @param {string} dateString - The UTC timestamp string
 * @param {Intl.DateTimeFormatOptions} options - Formatting options for toLocaleString
 * @returns {string} - Formatted date string or "—" if invalid
 */
export function formatUtcToLocal(dateString, options = {}) {
  const date = parseUtcDate(dateString);
  if (!date) return "—";
  return date.toLocaleString([], options);
}

/**
 * Formats a UTC timestamp string to a localized time string.
 * 
 * @param {string|Date} dateString - The UTC timestamp string or Date object
 * @param {Intl.DateTimeFormatOptions} options - Formatting options for toLocaleTimeString
 * @returns {string} - Formatted time string or "—" if invalid
 */
export function formatUtcToLocalTime(dateString, options = { hour: "2-digit", minute: "2-digit" }) {
  const date = parseUtcDate(dateString);
  if (!date) return "—";
  return date.toLocaleTimeString([], options);
}

/**
 * Calculates and formats the relative time ago from a UTC timestamp.
 * 
 * @param {string|Date} dateString - The UTC timestamp string or Date object
 * @returns {string} - Relative time string (e.g., "Just now", "5m ago", "2h ago", "3d ago")
 */
export function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const date = parseUtcDate(dateString);
  if (!date) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}
